import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Types based on the new tables
export interface HomePageSettings {
  id: string;
  hero_title_ar: string;
  hero_title_en: string | null;
  hero_subtitle_ar: string | null;
  hero_subtitle_en: string | null;
  hero_badge_text_ar: string | null;
  hero_badge_text_en: string | null;
  hero_cta_primary_text_ar: string | null;
  hero_cta_primary_text_en: string | null;
  hero_cta_primary_link: string | null;
  hero_cta_secondary_text_ar: string | null;
  hero_cta_secondary_text_en: string | null;
  hero_cta_secondary_link: string | null;
  show_categories_section: boolean;
  show_activities_section: boolean;
  show_how_it_works_section: boolean;
  show_testimonials_section: boolean;
  show_faq_section: boolean;
  hero_video_activity_id: string | null;
  hero_video_activity_ids: string[];
  created_at: string;
  updated_at: string;
}

export interface Testimonial {
  id: string;
  author_name_ar: string;
  author_name_en: string | null;
  author_role_ar: string | null;
  author_role_en: string | null;
  content_ar: string;
  content_en: string | null;
  avatar_url: string | null;
  rating: number;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface FAQ {
  id: string;
  question_ar: string;
  question_en: string | null;
  answer_ar: string;
  answer_en: string | null;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

// Home Page Settings Hooks
export function useHomePageSettings() {
  return useQuery({
    queryKey: ['home-page-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('home_page_settings')
        .select('*')
        .limit(1)
        .single();

      if (error) throw error;
      return data as HomePageSettings;
    },
    // Cache aggressively to prevent visible flicker between renders/route changes
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

export function useUpdateHomePageSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: Partial<HomePageSettings> & { id: string }) => {
      const { id, ...updates } = settings;
      const { data, error } = await supabase
        .from('home_page_settings')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['home-page-settings'] });
    },
  });
}

// Testimonials Hooks
export function useTestimonials(activeOnly = false) {
  return useQuery({
    queryKey: ['testimonials', { activeOnly }],
    queryFn: async () => {
      let query = supabase
        .from('testimonials')
        .select('*')
        .order('display_order', { ascending: true });

      if (activeOnly) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Testimonial[];
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

export function useCreateTestimonial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (testimonial: Omit<Testimonial, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('testimonials')
        .insert(testimonial)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
    },
  });
}

export function useUpdateTestimonial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (testimonial: Partial<Testimonial> & { id: string }) => {
      const { id, ...updates } = testimonial;
      const { data, error } = await supabase
        .from('testimonials')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
    },
  });
}

export function useDeleteTestimonial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('testimonials')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
    },
  });
}

// FAQ Hooks
export function useFAQs(activeOnly = false) {
  return useQuery({
    queryKey: ['faqs', { activeOnly }],
    queryFn: async () => {
      let query = supabase
        .from('faqs')
        .select('*')
        .order('display_order', { ascending: true });

      if (activeOnly) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as FAQ[];
    },
  });
}

export function useCreateFAQ() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (faq: Omit<FAQ, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('faqs')
        .insert(faq)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faqs'] });
    },
  });
}

export function useUpdateFAQ() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (faq: Partial<FAQ> & { id: string }) => {
      const { id, ...updates } = faq;
      const { data, error } = await supabase
        .from('faqs')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faqs'] });
    },
  });
}

export function useDeleteFAQ() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('faqs')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faqs'] });
    },
  });
}
